'use client';

import { useParams, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Twitter, Linkedin, Link2, Check, List } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTypedTranslation';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { getPostBySlug } from '@/lib/blog';
import { PageLayout } from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Meta } from '@/components/MetaManager';
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export default function BlogPost() {
  const { slug } = useParams({ from: '/blog/$slug' }) as { slug?: string };
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const language = i18n.language as 'en' | 'zh';
  const [copied, setCopied] = useState(false);
  const [activeId, setActiveId] = useState<string>('');
  const [showMobileToc, setShowMobileToc] = useState(false);
  

  const post = slug ? getPostBySlug(slug, language) : null;

  // 解析文章标题生成目录
  const tableOfContents = useMemo<TocItem[]>(() => {
    if (!post?.content) return [];
    
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    const toc: TocItem[] = [];
    let match;
    
    while ((match = headingRegex.exec(post.content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\u4e00-\u9fa5\s-]/g, '')
        .replace(/\s+/g, '-');
      
      toc.push({ id, text, level });
    }
    
    return toc;
  }, [post?.content]);

  // 计算阅读时间
  const readingTime = useMemo(() => {
    if (!post?.content) return 1;
    const wordCount = post.content.length;
    return Math.max(1, Math.ceil(wordCount / 500));
  }, [post?.content]);


  // 监听滚动更新当前活动标题
  useEffect(() => {
    if (tableOfContents.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -80% 0px' }
    );

    tableOfContents.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [tableOfContents]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      setShowMobileToc(false);
    }
  };

  if (!post) {
    return (
      <PageLayout>
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'zh' ? '文章未找到' : 'Post not found'}
            </h1>
            <Button 
              onClick={() => navigate({ to: getLocalizedPath('/blog') })}
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'zh' ? '返回博客' : 'Back to Blog'}
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const handleShare = async (platform: 'twitter' | 'linkedin' | 'copy') => {
    const url = window.location.href;
    const title = post.title;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          toast.success(language === 'zh' ? '链接已复制' : 'Link copied');
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error(language === 'zh' ? '复制失败' : 'Failed to copy');
        }
        break;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 自定义标题渲染器，添加id属性
  const headingRenderer = {
    h2: ({ children, ...props }: any) => {
      const text = String(children);
      const id = text
        .toLowerCase()
        .replace(/[^\w\u4e00-\u9fa5\s-]/g, '')
        .replace(/\s+/g, '-');
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }: any) => {
      const text = String(children);
      const id = text
        .toLowerCase()
        .replace(/[^\w\u4e00-\u9fa5\s-]/g, '')
        .replace(/\s+/g, '-');
      return <h3 id={id} {...props}>{children}</h3>;
    },
  };

  const breadcrumbs = generateBreadcrumbs([
    { label: language === 'zh' ? '博客' : 'Blog', path: '/blog' },
    { label: post.title, path: `/blog/${slug}` }
  ], language);

  return (
    <PageLayout>
      <Meta
        title={`${post.title} | borninsea`}
        description={post.description || post.title}
        keywords={post.tags?.join(',') || ''}
        image={post.coverImage}
        type="article"
        author={post.author || '王海生'}
        publishedTime={post.date}
      />
      <StructuredData 
        type="article" 
        breadcrumbs={breadcrumbs}
        article={{
          title: post.title,
          description: post.description || post.title,
          image: post.coverImage,
          datePublished: post.date,
          dateModified: post.date,
          author: post.author || '王海生'
        }}
      />
      <div className="min-h-screen bg-white">
        {/* 文章头部 */}
        <motion.header 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="pt-28 pb-8 text-center max-w-4xl mx-auto px-6"
        >
          {/* 面包屑导航 */}
          <div className="flex items-center justify-center gap-3 text-sm mb-6">
            {post.tags && post.tags.length > 0 && (
              <>
                <Link 
                  to={getLocalizedPath('/blog')}
                  className="text-primary hover:underline font-medium"
                >
                  {post.tags[0]}
                </Link>
                <span className="text-gray-400">•</span>
              </>
            )}
            <span className="text-gray-500">
              {readingTime} {language === 'zh' ? '分钟阅读' : 'min read'}
            </span>
          </div>

          {/* 标题 */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
            {post.title}
          </h1>

          {/* 描述/副标题 */}
          {post.description && (
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {post.description}
            </p>
          )}

          {/* 作者和日期 */}
          <div className="text-sm text-gray-500 mb-8">
            <span>By </span>
            <span className="font-medium text-gray-700">{post.author || 'TCM'}</span>
            <span className="mx-2">·</span>
            <span>{formatDate(post.date)}</span>
          </div>

          {/* 社交分享按钮 */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => handleShare('twitter')}
              className="p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Share on Twitter"
            >
              <Twitter className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleShare('linkedin')}
              className="p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Share on LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleShare('copy')}
              className="p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Copy link"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
            </button>
          </div>
        </motion.header>

        {/* 封面图片 */}
        {post.coverImage && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-4xl mx-auto px-6 mb-12"
          >
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-auto rounded-xl shadow-sm"
            />
          </motion.div>
        )}

        {/* 主体内容区域 */}
        <div className="max-w-7xl mx-auto px-6 pb-16">
          <div className="flex gap-12">
            {/* 文章内容 */}
            <motion.article 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 max-w-3xl"
            >
              <div className="prose prose-lg prose-gray max-w-none
                prose-headings:text-gray-900 prose-headings:font-bold prose-headings:scroll-mt-24
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900
                prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-normal prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:overflow-x-auto
                prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:italic prose-blockquote:text-gray-700
                prose-ul:my-6 prose-li:text-gray-700 prose-li:my-2
                prose-img:rounded-lg prose-img:shadow-sm
              ">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={headingRenderer}
                >
                  {post.content}
                </ReactMarkdown>
              </div>
            </motion.article>

            {/* 侧边栏目录 - 桌面端 */}
            {tableOfContents.length > 0 && (
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <nav className="sticky top-28">
                  <div className="border-l border-gray-200 pl-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">
                      {language === 'zh' ? '目录' : 'On this page'}
                    </h4>
                    <ul className="space-y-2">
                      {tableOfContents.map((item) => (
                        <li key={item.id}>
                          <button
                            onClick={() => scrollToHeading(item.id)}
                            className={cn(
                              "text-sm text-left w-full transition-colors hover:text-gray-900",
                              item.level === 3 && "pl-3",
                              activeId === item.id
                                ? "text-primary font-medium"
                                : "text-gray-500"
                            )}
                          >
                            {item.text}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </nav>
              </aside>
            )}
          </div>
        </div>

        {/* 移动端目录按钮 */}
        {tableOfContents.length > 0 && (
          <>
            <button
              onClick={() => setShowMobileToc(!showMobileToc)}
              className="lg:hidden fixed bottom-6 right-6 z-40 p-3 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors"
              aria-label="Toggle table of contents"
            >
              <List className="w-5 h-5" />
            </button>

            {/* 移动端目录面板 */}
            {showMobileToc && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="lg:hidden fixed bottom-20 right-6 z-40 w-72 max-h-80 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 p-4"
              >
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  {language === 'zh' ? '目录' : 'On this page'}
                </h4>
                <ul className="space-y-2">
                  {tableOfContents.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToHeading(item.id)}
                        className={cn(
                          "text-sm text-left w-full transition-colors hover:text-gray-900",
                          item.level === 3 && "pl-3",
                          activeId === item.id
                            ? "text-primary font-medium"
                            : "text-gray-600"
                        )}
                      >
                        {item.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </>
        )}

        {/* 底部导航 */}
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: getLocalizedPath('/blog') })}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'zh' ? '返回博客' : 'Back to Blog'}
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 mr-2">
                {language === 'zh' ? '分享文章' : 'Share'}
              </span>
              <button
                onClick={() => handleShare('twitter')}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Twitter className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
